import React, { useMemo, useState } from "react";
import { CaretDown, MagnifyingGlass, Check } from "@phosphor-icons/react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

/**
 * Generic searchable combobox with client-side dedupe.
 *
 * Configure per-domain by supplying:
 *   • `options`         — array of records (e.g., products / inventory items / recipes)
 *   • `getId`           — record → the stable id passed to `onPick`
 *   • `getKey`          — record → the dedupe key (defaults to `getId`)
 *   • `getLabel`        — record → primary text
 *   • `getSecondary`    — record → optional secondary text (SKU · price, code · uom, …)
 *   • `getSearchFields` — record → array of lowercased strings the query filters against
 *   • `sortNewestBy`    — field name to break ties on dedupe (newest wins)
 *
 * This component powers ProductCombobox / InventoryItemCombobox / RecipeCombobox.
 * Keeping the primitives in one place means the UX (open-on-click, live filter,
 * empty-state, checkmark, popover width) stays consistent across the app.
 */
export default function SearchableCombobox({
  options = [],
  value = "",
  onPick,
  testId = "combobox",
  placeholder = "Search…",
  emptyLabel = "No matches.",
  disabled = false,
  className = "",
  getId,
  getKey,
  getLabel,
  getSecondary,
  getSearchFields,
  sortNewestBy = "updated_at",
  maxResults = 200,
}) {
  const _id = getId || ((r) => r?.id);
  const _key = getKey || ((r) => _id(r));
  const _label = getLabel || ((r) => r?.name || _id(r) || "");
  const _secondary = getSecondary || (() => "");
  const _search = getSearchFields || ((r) => [_label(r)]);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const unique = useMemo(() => {
    const seen = new Map();
    const sorted = [...options].sort(
      (a, b) =>
        new Date(b?.[sortNewestBy] || 0).getTime() -
        new Date(a?.[sortNewestBy] || 0).getTime()
    );
    for (const r of sorted) {
      const k = String(_key(r) || "").trim();
      if (!k) continue;
      if (!seen.has(k)) seen.set(k, r);
    }
    return Array.from(seen.values()).sort((a, b) =>
      String(_label(a)).localeCompare(String(_label(b)))
    );
  }, [options, sortNewestBy, _key, _label]);

  const selected = unique.find((r) => _id(r) === value);
  const q = query.trim().toLowerCase();

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={
            "h-9 mt-1 w-full inline-flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-3 text-left text-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 " +
            className
          }
        >
          <span className={`truncate ${selected ? "text-slate-900" : "text-slate-400"}`}>
            {selected ? _label(selected) : placeholder}
          </span>
          <CaretDown size={14} weight="bold" className="text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[--radix-popover-trigger-width] min-w-[260px] p-0"
        data-testid={`${testId}-popover`}
      >
        <Command shouldFilter={false}>
          <div className="flex items-center gap-2 px-3 border-b border-slate-100">
            <MagnifyingGlass size={14} className="text-slate-400 shrink-0" />
            <CommandInput
              value={query}
              onValueChange={setQuery}
              placeholder="Type to search…"
              className="h-10 border-0 focus:ring-0 text-sm"
              data-testid={`${testId}-input`}
            />
          </div>
          <CommandList className="max-h-64">
            <CommandEmpty className="py-6 text-center text-xs text-slate-400">
              {emptyLabel}
            </CommandEmpty>
            <CommandGroup>
              {(() => {
                // Filter FIRST, then slice — otherwise a large collection can
                // silently drop matches that live past the max-results cap.
                const matches = q
                  ? unique.filter((r) =>
                      _search(r).some((s) =>
                        String(s || "").toLowerCase().includes(q)
                      )
                    )
                  : unique;
                return matches.slice(0, maxResults).map((r) => {
                  const id = _id(r);
                  const secondary = _secondary(r);
                  return (
                    <CommandItem
                      key={id}
                      value={String(id)}
                      onSelect={() => {
                        onPick?.(id);
                        setQuery("");
                        setOpen(false);
                      }}
                      className="flex items-center justify-between gap-3 cursor-pointer"
                      data-testid={`${testId}-option-${id}`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm text-slate-900 truncate">{_label(r)}</div>
                        {secondary && (
                          <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5 truncate">
                            {secondary}
                          </div>
                        )}
                      </div>
                      {value === id && (
                        <Check size={14} weight="bold" className="text-blue-600 shrink-0" />
                      )}
                    </CommandItem>
                  );
                });
              })()}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
